import * as React from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Typography, Button } from 'components/common';
import InputPassword from 'components/form/input-password/input-password';
import AppRoutes from '@/const/routes';
import { useNotification } from '@/hooks';
import { clearStorage, formatErrorMessage, signOutLawyer } from '@/lib';
import { LayoutLogin } from '@/components/layout';
import { passwordCriteria } from '@/const';
import { userService } from '@/api';
import { PasswordPayload } from '@/interfaces';
import { useRouter } from 'next/router';
import { deleteLocalStorage, getLocalStorage } from '@/utils/localStorage';

export const SetNewPasswordScreen: React.FC = (): JSX.Element => {
	const router = useRouter();
	const { toastError, toastSuccess } = useNotification();
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const {
		register,
		handleSubmit,
		watch,
		formState: { errors, isDirty, isValid },
	} = useForm({ mode: 'onChange' });

	// Inputs rules
	const rules = {
		password: {
			required: {
				value: true,
				message: 'This is required.',
			},
		},
		password_confirm: {
			required: {
				value: true,
				message: 'This is required.',
			},
			validate: (value: string) =>
				value === watch('password') ||
				'The passwords do not match. Please ensure they are identical.',
		},
	};

	// Sign in with Data
	const handleSubmitDataForm = async (data: FieldValues): Promise<void> => {
		setIsLoading(true);
		try {
			const currentPassword = getLocalStorage('temporal-password');
			const newData: PasswordPayload = {
				currentPassword: currentPassword,
				password: data.password,
				confirmPassword: data.password_confirm,
			};
			await userService.updatePassword(newData);
			deleteLocalStorage('temporal-password');
			toastSuccess(`Your password has been successfully set.`);
			router.push(AppRoutes.LAWYERAPP_AUTH_COMPLETE_ACCOUNT_DETAILS);
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<LayoutLogin
			title={'Set Your New Password'}
			rol="lawyer"
			handleBack={async () => {
				await clearStorage();
				signOutLawyer();
			}}
		>
			<form onSubmit={handleSubmit(handleSubmitDataForm)} className="w-full">
				<InputPassword
					name="password"
					title={'New Password'}
					validate={true}
					register={register}
					rules={rules.password}
					error={errors.password}
					className="mb-4"
				/>
				{!errors.password && (
					<Typography type="regular-12" className="text-opacity-black">
						{passwordCriteria}
					</Typography>
				)}
				<InputPassword
					name="password_confirm"
					title={'Confirm New Password'}
					validate={false}
					register={register}
					rules={rules.password_confirm}
					error={errors.password_confirm}
					className="mt-4"
				/>
				<Button
					type="submit"
					size="full"
					decoration="primary"
					className="mt-10"
					disabled={isLoading || !isDirty || !isValid}
				>
					{'Set Your New Password'}
				</Button>
			</form>
		</LayoutLogin>
	);
};
